/**
 * One classification of a thrown error, over Phase 2's narrowing helpers. Pure — the renderer
 * (`ProblemAlert`) is Phase 8.
 */
import type { Problem } from "../api/client";
import {
  isApiError,
  isConflict,
  isDerivedOutputPending,
  isDerivedOutputUnavailable,
  isNotFound,
  isTenantNotResolved,
  isUnsupportedUploadFormat,
  isValidation,
  problemOf,
} from "../api/errors";

export type ProblemKind =
  | "unauthenticated"
  | "forbidden"
  | "not_found"
  /**
   * The two 404s that mean "this run's derived output is not here YET" and "…and never will be"
   * (IS-D15 / AN-D11). Separate kinds because the surfaces answer them differently: `pending`
   * refetches on `DERIVED_OUTPUT_RETRY_MS`, `unavailable` is terminal. Neither is `not_found`,
   * and neither is the 200-with-no-rows that means a genuinely clean plan.
   */
  | "derived_pending"
  | "derived_unavailable"
  | "tenant_not_resolved"
  | "conflict"
  | "validation"
  | "unsupported_upload"
  | "server"
  | "network";

export interface ClassifiedProblem {
  kind: ProblemKind;
  problem: Problem | null;
  /** Set on `forbidden` when the API named the permission the caller lacks. */
  requiredPermission?: string;
  /** 422 only: keyed by the LAST segment of each `errors[].loc`. */
  fieldErrors?: Record<string, string>;
  /** 422 only: messages whose `loc` tail names no field on the form's body. */
  unmapped?: string[];
}

export function classifyProblem(e: unknown): ClassifiedProblem {
  const problem = problemOf(e);

  /* No Problem means the request never reached the API (offline, DNS, CORS, abort). */
  if (!isApiError(e) || !problem) return { kind: "network", problem: null };

  if (e.isUnauthenticated) return { kind: "unauthenticated", problem };
  if (e.isForbidden) {
    return problem.requiredPermission
      ? { kind: "forbidden", problem, requiredPermission: problem.requiredPermission }
      : { kind: "forbidden", problem };
  }
  if (isTenantNotResolved(e)) return { kind: "tenant_not_resolved", problem };
  /* Both are 404s, so they are read BEFORE `not_found`, which excludes them by construction. */
  if (isDerivedOutputPending(e)) return { kind: "derived_pending", problem };
  if (isDerivedOutputUnavailable(e)) return { kind: "derived_unavailable", problem };
  if (isNotFound(e)) return { kind: "not_found", problem };
  if (isConflict(e)) return { kind: "conflict", problem };
  if (isUnsupportedUploadFormat(e)) return { kind: "unsupported_upload", problem };

  const validation = isValidation(e);
  if (validation) {
    const fieldErrors: Record<string, string> = {};
    const unmapped: string[] = [];
    for (const entry of validation) {
      const tail = entry.loc[entry.loc.length - 1];
      if (tail === undefined || typeof tail === "number") {
        unmapped.push(entry.msg);
        continue;
      }
      /* First message per field wins: a form shows one message under one input. */
      if (!(tail in fieldErrors)) fieldErrors[tail] = entry.msg;
    }
    return { kind: "validation", problem, fieldErrors, unmapped };
  }

  return { kind: "server", problem };
}

/**
 * A 422 that names no field: `InvalidRateRange`, or an overlay write's "that row is not in the base
 * package". Its words are in `detail`, but `ProblemAlert` renders a validation problem from its
 * per-field messages only, so a field-less 422 would read as a bare title. This copies `detail`
 * into `unmapped` for it, and returns every other problem as it came.
 */
export function surfaceDetail(problem: ClassifiedProblem | null): ClassifiedProblem | null {
  if (!problem || problem.kind !== "validation") return problem;
  const hasFieldMessage =
    Object.keys(problem.fieldErrors ?? {}).length > 0 || (problem.unmapped ?? []).length > 0;
  const detail = problem.problem?.detail;
  if (hasFieldMessage || !detail) return problem;
  return { ...problem, unmapped: [detail] };
}
